import { Input } from "@/components/ui/input";
import OrganismSearch from "@/components/common/OrganismSearch";
import AlignmentToolSelect from "@/components/common/AlignmentToolSelect";
import MetadataBuilder from "@/components/common/MetadataBuilder";

const MIN_PHRED_QUALITY_SCORE = 10;
const MAX_PHRED_QUALITY_SCORE = 40;
const MIN_READ_LENGTH = 50;
const MAX_READ_LENGTH = 120;

export const defaultValues = {
  organism: "",
  alignment_tool: "",
  raw_data_qc: {
    phred_quality_score: 30,
    minimum_read_length_required: 75,
  },
  metadata_builder: [
    {
      sample_id: "",
    },
  ],
};
export const validate = (values = {}) => {
  const errors = {};
  const rawDataQc = values.raw_data_qc || {};

  if (!String(values.organism || "").trim()) {
    errors.organism = "Organism is required.";
  }
  if (!String(values.alignment_tool || "").trim()) {
    errors.alignment_tool = "Alignment tool is required.";
  }

  if (rawDataQc.phred_quality_score === "" || rawDataQc.phred_quality_score === null || rawDataQc.phred_quality_score === undefined) {
    errors.phred_quality_score = "Phred quality score is required.";
  } else {
    const numericValue = Number(rawDataQc.phred_quality_score);
    if (Number.isNaN(numericValue) || numericValue < MIN_PHRED_QUALITY_SCORE || numericValue > MAX_PHRED_QUALITY_SCORE) {
      errors.phred_quality_score = `Value must be between ${MIN_PHRED_QUALITY_SCORE} and ${MAX_PHRED_QUALITY_SCORE}.`;
    }
  }

  if (rawDataQc.minimum_read_length_required === "" || rawDataQc.minimum_read_length_required === null || rawDataQc.minimum_read_length_required === undefined) {
    errors.minimum_read_length_required = "Minimum read length is required.";
  } else {
    const numericValue = Number(rawDataQc.minimum_read_length_required);
    if (Number.isNaN(numericValue) || numericValue < MIN_READ_LENGTH || numericValue > MAX_READ_LENGTH) {
      errors.minimum_read_length_required = `Value must be between ${MIN_READ_LENGTH} and ${MAX_READ_LENGTH}.`;
    }
  }


  const metadata = values.metadata_builder || [];

  if (metadata.length === 0) {
    errors.metadata_builder = "At least one sample is required.";
  } else {
    const sampleIds = new Set();

    metadata.forEach((row, index) => {
      const sampleId = String(row.sample_id || "").trim();

      if (!sampleId) {
        errors[`sample_id_${index}`] = "Sample ID is required.";
      } else if (sampleIds.has(sampleId.toLowerCase())) {
        errors[`sample_id_${index}`] = "Duplicate Sample ID.";
      } else {
        sampleIds.add(sampleId.toLowerCase());
      }
    });
  }

  return errors;
};

export default function VariantAnalysisParameters({
  values = {},
  onChange,
  errors = {},
  expectedSamples,
}) {
  const setValue = (key, value) => onChange(key, value);

  const updateRawDataQc = (key, rawValue) => {
    if (rawValue === "") {
      setValue("raw_data_qc", { ...(values.raw_data_qc || {}), [key]: "" });
      return;
    }

    const numericValue = Number(rawValue);
    if (Number.isNaN(numericValue)) return;

    setValue("raw_data_qc", { ...(values.raw_data_qc || {}), [key]: numericValue });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAF7] p-4">
        <h3 className="text-sm font-semibold text-[#09582D]">Basic Information</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Organism</label>
            <OrganismSearch
              value={values.organism || ""}
              onChange={(organism) =>
                setValue("organism", organism?.renderName)
              }
            />
            {errors.organism && <p className="mt-1 text-xs text-red-600">{errors.organism}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Alignment Tool</label>
            <AlignmentToolSelect
              value={values.alignment_tool || ""}
              onChange={(value) => setValue("alignment_tool", value)}
              options={["BWA", "Bowtie2"]}
            />
            {errors.alignment_tool && <p className="mt-1 text-xs text-red-600">{errors.alignment_tool}</p>}
          </div>
        </div>
      </div>
      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <MetadataBuilder
          value={values.metadata_builder || []}
          onChange={(metadata) => setValue("metadata_builder", metadata)}
          errors={errors}
          expectedSamples={expectedSamples}
        />
      </div>

      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-gray-800">Raw Data QC Parameters</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phred Quality Score</label>
            <Input
              type="number"
              min={MIN_PHRED_QUALITY_SCORE}
              max={MAX_PHRED_QUALITY_SCORE}
              value={values.raw_data_qc?.phred_quality_score ?? ""}
              onChange={(e) => updateRawDataQc("phred_quality_score", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            />
            <p className="mt-1 text-xs text-gray-500">Allowed range: {MIN_PHRED_QUALITY_SCORE} to {MAX_PHRED_QUALITY_SCORE}</p>
            {errors.phred_quality_score && <p className="mt-1 text-xs text-red-600">{errors.phred_quality_score}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Minimum Read Length Required</label>
            <Input
              type="number"
              min={MIN_READ_LENGTH}
              max={MAX_READ_LENGTH}
              value={values.raw_data_qc?.minimum_read_length_required ?? ""}
              onChange={(e) => updateRawDataQc("minimum_read_length_required", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            />
            <p className="mt-1 text-xs text-gray-500">Allowed range: {MIN_READ_LENGTH} to {MAX_READ_LENGTH}</p>
            {errors.minimum_read_length_required && <p className="mt-1 text-xs text-red-600">{errors.minimum_read_length_required}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
